import React, { useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  IconButton,
  TextField,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
  GEN_COND_LABEL,
  GEN_COND_COLOR,
  DISCIPLINE_COLOR_PALETTE,
  deriveAbbreviation,
  disciplineBadge,
} from './discipline-badge';

/**
 * Left pane: start a new conversation, select among every conversation for
 * the current project (regardless of discipline -- discipline is no longer
 * a page-level filter, see ProjectDocumentsAssistantPage.js), and rename one
 * in place.
 *
 * Each row carries a small colored classification Chip, positioned BEFORE
 * the conversation title (leftmost) -- "GC" in its own fixed color for a
 * conversation whose INITIAL classification was project-wide (disciplineId
 * === null), or a real discipline's short label (see discipline-badge.js's
 * KNOWN_SHORT_LABELS, e.g. "Mechanical" -> "MECH") in an id-hashed color
 * otherwise -- resolved from `disciplines` (the full, unfiltered list; a
 * conversation legitimately classified under the real "General Conditions"
 * discipline row must show ITS OWN badge here, never be conflated with the
 * synthetic GC badge -- see the collision guard in `scopeBadge` below).
 * This mirrors the small
 * colored MUI Chip idiom used for TimecardDownload's own location_type
 * column (FIELD/SHOP), rather than inventing a new visual style. The badge
 * ALWAYS reads the conversation's disciplineId directly from `conversations`
 * (never from any in-flight/pending selector value elsewhere in the app,
 * and never from the conversation's SEPARATE, mutable
 * searchScopeDisciplineId -- see ProjectDocumentsAssistantPage.js) -- it is
 * the one place a conversation's durable, IMMUTABLE INITIAL classification
 * is displayed in the sidebar. Changing a conversation's current search
 * scope later (the composer selector, driven by searchScopeDisciplineId)
 * never changes this badge.
 *
 * Abbreviation/color are entirely presentation-only, derived client-side
 * from data already on hand (a discipline's name and id) -- there is no
 * abbreviation/color column on ai_knowledge_disciplines, and none should be
 * added.
 *
 * Renaming is local UI state owned entirely by this component (which row is
 * being edited, and its draft text) -- the parent
 * (ProjectDocumentsAssistantPage) owns the actual API call, the
 * request-generation tracking, and the in-flight/error state it hands back
 * via renamingConversationId/renameError (local form state; the parent owns
 * the request). onRenameConversation resolves to a boolean: true closes
 * this row's edit state, false leaves it open so renameError (from the
 * parent) stays visible next to it.
 */
/**
 * Resolves a conversation's classification badge from the conversation's
 * OWN disciplineId (its immutable initial classification; see the module
 * comment above for why this must never come from an in-flight selector
 * value, or from searchScopeDisciplineId, elsewhere in the app). The actual
 * label/color derivation is shared with ChatPanel's compact scope selector
 * -- see discipline-badge.js.
 */
const scopeBadge = (conversation, disciplines) =>
  disciplineBadge(conversation.disciplineId, disciplines);

const ConversationSidebar = ({
  conversations,
  conversationsLoading,
  conversationId,
  disciplines = [],
  renamingConversationId,
  renameError,
  onNewConversation,
  onSelectConversation,
  onRenameConversation,
}) => {
  const [editingId, setEditingId] = useState(null);
  const [draftTitle, setDraftTitle] = useState('');

  const startEditing = (conversation) => {
    setEditingId(conversation.id);
    setDraftTitle(conversation.title || '');
  };

  const cancelEditing = () => {
    setEditingId(null);
    setDraftTitle('');
  };

  const saveEditing = async (id) => {
    const trimmed = draftTitle.trim();
    if (!trimmed) return;
    const succeeded = await onRenameConversation(id, trimmed);
    if (succeeded) {
      setEditingId(null);
      setDraftTitle('');
    }
  };

  return (
    <Box
      data-testid="conversation-sidebar"
      sx={{
        width: 260,
        flexShrink: 0,
        borderRight: 1,
        borderColor: 'divider',
        bgcolor: '#fafbfc',
        p: 1.5,
        overflowY: 'auto',
        minWidth: 0,
      }}
    >
      <Button
        fullWidth
        variant="contained"
        size="small"
        startIcon={<AddIcon fontSize="small" />}
        onClick={onNewConversation}
        sx={{ mb: 1.75, fontWeight: 600, letterSpacing: 0.4 }}
      >
        New Chat
      </Button>

      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: 'text.secondary',
          letterSpacing: 0.3,
          px: 0.5,
          pb: 1,
        }}
      >
        CONVERSATIONS
      </Typography>

      {conversationsLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {!conversationsLoading && conversations.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>
          No conversations yet.
        </Typography>
      )}

      {!conversationsLoading &&
        conversations.map((conversation) => {
          const selected = conversation.id === conversationId;
          const isEditing = editingId === conversation.id;
          const isRenaming = renamingConversationId === conversation.id;
          const badge = scopeBadge(conversation, disciplines);

          if (isEditing) {
            return (
              <Box key={conversation.id} sx={{ mb: 0.4, px: 0.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TextField
                    value={draftTitle}
                    onChange={(e) => setDraftTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        saveEditing(conversation.id);
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        cancelEditing();
                      }
                    }}
                    size="small"
                    autoFocus
                    fullWidth
                    disabled={isRenaming}
                    inputProps={{ 'aria-label': 'Conversation name' }}
                    sx={{ '& .MuiInputBase-input': { fontSize: 13, py: 0.6 } }}
                  />
                  <IconButton
                    size="small"
                    aria-label="Save conversation name"
                    disabled={isRenaming || !draftTitle.trim()}
                    onClick={() => saveEditing(conversation.id)}
                  >
                    {isRenaming ? (
                      <CircularProgress size={14} />
                    ) : (
                      <CheckIcon fontSize="small" />
                    )}
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label="Cancel rename"
                    disabled={isRenaming}
                    onClick={cancelEditing}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
                {renameError && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ display: 'block', px: 0.5, pt: 0.25 }}
                  >
                    {renameError}
                  </Typography>
                )}
              </Box>
            );
          }

          return (
            <Box
              key={conversation.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                mb: 0.4,
              }}
            >
              <Box
                role="button"
                tabIndex={0}
                onClick={() => onSelectConversation(conversation.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelectConversation(conversation.id);
                  }
                }}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  cursor: 'pointer',
                  borderRadius: 1.5,
                  px: 1.25,
                  py: 1.1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  bgcolor: selected ? '#e8f0fe' : 'transparent',
                  color: selected ? '#1a56c4' : 'text.primary',
                  '&:hover': { bgcolor: selected ? '#e8f0fe' : 'action.hover' },
                }}
              >
                <Chip
                  label={badge.label}
                  size="small"
                  sx={{
                    flexShrink: 0,
                    height: 16,
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: 0.3,
                    color: '#fff',
                    bgcolor: badge.color,
                    opacity: selected ? 0.85 : 1,
                    '& .MuiChip-label': { px: 0.7 },
                  }}
                />
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 13,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                  }}
                  title={conversation.title || 'Untitled conversation'}
                >
                  {conversation.title || 'Untitled conversation'}
                </Box>
              </Box>
              <IconButton
                size="small"
                aria-label="Rename conversation"
                onClick={(e) => {
                  e.stopPropagation();
                  startEditing(conversation);
                }}
              >
                <EditIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Box>
          );
        })}
    </Box>
  );
};

// Named exports below are for direct unit testing of the abbreviation/color
// derivation (see ConversationSidebar.test.js) -- the component itself only
// ever consumes them internally via scopeBadge.
export {
  GEN_COND_LABEL,
  GEN_COND_COLOR,
  DISCIPLINE_COLOR_PALETTE,
  deriveAbbreviation,
  scopeBadge,
};

export default ConversationSidebar;
